import type { TOC } from '@ember/component/template-only';

interface DisclosureToggleSignature {
  Args: {
    /** Expands the chevron without an aria-expanded ancestor; a true ancestor also expands it. */
    expanded?: boolean;
    /**
     * Drop the chamfered face and render the chevron alone, inheriting the
     * surrounding text colour. For inline text toggles ("Details ⌄" beside a
     * link), where a face would turn a text affordance into a control.
     */
    bare?: boolean;
  };
  Element: HTMLSpanElement;
}

/**
 * DisclosureToggle - the chamfered control at the trailing end of a disclosure row.
 *
 * Renders a `--bg-sunken` face wearing `--edge-chamfer` around a chevron whose two
 * half-strokes pivot about their shared apex, so opening sweeps the mark flat before
 * it re-bends. Presentational only: the caller owns the `<button>`, the click handler
 * and `aria-expanded`. The chevron reads `aria-expanded` off that ancestor button, so
 * `@expanded` only needs passing when no such ancestor exists.
 *
 * @example Inside a disclosure row
 * ```gts
 * <button type="button" class="tio-row-between w-full" aria-expanded={{if this.isOpen "true" "false"}}>
 *   <Text>{{faq.question}}</Text>
 *   <DisclosureToggle @expanded={{this.isOpen}} />
 * </button>
 * ```
 */
const DisclosureToggle: TOC<DisclosureToggleSignature> = <template>
  <span
    data-test-disclosure-toggle
    class="tio-disclosure-toggle{{if @bare ' tio-disclosure-toggle--bare'}}"
    data-expanded={{if @expanded "true" "false"}}
    ...attributes
  >
    {{! Two half-strokes, not one path: the shared apex at 0,0 is the pivot, and sweeping
        both through 0deg flattens the chevron to a bar mid-transition. }}
    <svg class="tio-chevron" viewBox="-9 -9 18 18" fill="none" aria-hidden="true">
      <line class="tio-chevron__l" x1="-6" y1="0" x2="0" y2="0" />
      <line class="tio-chevron__r" x1="0" y1="0" x2="6" y2="0" />
    </svg>
  </span>
</template>;

export default DisclosureToggle;

export type { DisclosureToggleSignature };
